import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Landing-system FooterBase.
 *
 * Shared structure used by every landing surface: brand block (logo + tagline)
 * + N link columns + bottom © line. Brand glyph and labels are passed in so
 * each app picks its own. Routing convention is unified: `#`, `http`, `mailto:`
 * use <a>; everything else uses react-router <Link>.
 *
 * Tone defaults to 'dark' (slate-950) which matches all current landing pages.
 * 'light' is reserved for future Katalag light-mode footer if needed.
 */
export type LandingFooterTone = 'dark' | 'light';

export type LandingFooterLink = { label: string; href: string };
export type LandingFooterColumn = { heading: string; links: LandingFooterLink[] };

type Props = {
  tone?: LandingFooterTone;
  /** Brand block: logo glyph + brand name. Wrapped in a `<Link to="/">` by caller. */
  brand: React.ReactNode;
  tagline?: string;
  columns: LandingFooterColumn[];
  copyright: string;
  className?: string;
};

const toneShell: Record<LandingFooterTone, string> = {
  dark: 'border-white/10 bg-slate-950 text-white',
  light: 'border-slate-200 bg-white text-slate-950',
};

const toneHeading: Record<LandingFooterTone, string> = {
  dark: 'text-white/40',
  light: 'text-slate-500',
};

const toneLink: Record<LandingFooterTone, string> = {
  dark: 'text-white/70 hover:text-white',
  light: 'text-slate-600 hover:text-slate-950',
};

const toneTagline: Record<LandingFooterTone, string> = {
  dark: 'text-white/70',
  light: 'text-slate-600',
};

const toneCopy: Record<LandingFooterTone, string> = {
  dark: 'border-white/10 text-white/50',
  light: 'border-slate-200 text-slate-500',
};

/**
 * Maps the number of link columns -> tailwind grid template. We always reserve
 * the first track for the brand block (1.2fr) and split remaining space evenly.
 * Falls back to a sensible default beyond 5 columns.
 */
const colsTemplate: Record<number, string> = {
  1: 'md:grid-cols-[1.2fr_1fr]',
  2: 'md:grid-cols-[1.2fr_repeat(2,1fr)]',
  3: 'md:grid-cols-[1.2fr_repeat(3,1fr)]',
  4: 'md:grid-cols-[1.2fr_repeat(4,1fr)]',
  5: 'md:grid-cols-[1.2fr_repeat(5,1fr)]',
};

export default function LandingFooterBase({
  tone = 'dark',
  brand,
  tagline,
  columns,
  copyright,
  className = '',
}: Props) {
  const grid = colsTemplate[columns.length] ?? 'md:grid-cols-[1.2fr_repeat(3,1fr)]';
  return (
    <footer className={`border-t ${toneShell[tone]} ${className}`.trim()}>
      <div className={`mx-auto grid max-w-7xl gap-10 px-6 py-14 ${grid}`}>
        <div>
          {brand}
          {tagline ? (
            <p className={`mt-4 max-w-sm leading-7 ${toneTagline[tone]}`}>{tagline}</p>
          ) : null}
        </div>
        {columns.map((column) => (
          <div key={column.heading}>
            <div
              className={`text-sm font-semibold uppercase tracking-[0.18em] ${toneHeading[tone]}`}
            >
              {column.heading}
            </div>
            <div className="mt-4 space-y-3">
              {column.links.map((link) => {
                const cls = `block transition ${toneLink[tone]}`;
                if (
                  link.href.startsWith('#') ||
                  link.href.startsWith('http') ||
                  link.href.startsWith('mailto:')
                ) {
                  return (
                    <a key={link.label} href={link.href} className={cls}>
                      {link.label}
                    </a>
                  );
                }
                return (
                  <Link key={link.label} to={link.href} className={cls}>
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className={`border-t px-6 py-6 text-center text-sm ${toneCopy[tone]}`}>
        {copyright}
      </div>
    </footer>
  );
}